import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import type Filter from './Filter';
import SegmentFilterManager from './SegmentFilterManager';
import { SegmentId } from '../../utils/segments';
import { AnyAction } from 'redux-saga';
import { extractFmsSlug } from '../../utils';

const segmentFilterManager = SegmentFilterManager.getInstance();

export const initialState: Filter.ReducerState = {
  page: 1,
  size: 15,
  segment: 'main',
};

export const categoryMappingInitialState: Filter.CategoryMappingState = {
  idToSlug: { 0: 'uncategorized' },
  slugToId: { uncategorized: 0 },
  isReady: false,
  pendingCategorySlugs: null,
};

export const categoryMappingSlice = createSlice({
  name: 'categoryMapping',
  initialState: categoryMappingInitialState,
  reducers: {
    setCategories(
      state,
      action: PayloadAction<
        {
          id: number;
          fmsKey: string;
        }[]
      >
    ) {
      state.idToSlug = { 0: 'uncategorized' };
      state.slugToId = { uncategorized: 0 };

      action.payload.forEach((category) => {
        const slug = extractFmsSlug(category.fmsKey);
        state.idToSlug[category.id] = slug;
        state.slugToId[slug] = category.id;
      });

      state.isReady = true;
    },
    setPendingCategorySlugs(state, action: PayloadAction<string[] | null>) {
      state.pendingCategorySlugs = action.payload;
    },
    clearPendingCategorySlugs(state) {
      state.pendingCategorySlugs = null;
    },
  },
});
export const { actions: categoryMappingActions } = categoryMappingSlice;
export const categoryMappingReducer = categoryMappingSlice.reducer;

const baseReducer = <T>(
  reducer: (state: Filter.ReducerState, action: PayloadAction<T>) => void
) => ({
  reducer,
  prepare: (payload: T) => ({ payload, meta: { resetsPage: true } }),
});

const baseReducerNoPayload = (
  reducer: (state: Filter.ReducerState) => void
) => ({
  reducer,
  prepare: () => ({ payload: undefined, meta: { resetsPage: true } }),
});

export const filterSlice = createSlice({
  name: 'filter',
  initialState,
  reducers: {
    changePage(state, action: Filter.ChangePageAction) {
      state.page = action.payload;
    },
    changeSortBy: baseReducer<Filter.ChangeSortByAction['payload']>(
      (state, action) => {
        if (state.sortBy === action.payload) {
          state.sortDirection = state.sortDirection === 'ASC' ? 'DESC' : 'ASC';
        } else {
          state.sortBy = action.payload;
          state.sortDirection = 'ASC';
        }
      }
    ),
    changeSegment: baseReducer<Filter.ChangeSegmentAction['payload']>(
      (state, action) => {
        const oldSegment = state.segment as SegmentId;
        const newSegment = action.payload as SegmentId;

        // save current filters
        segmentFilterManager.saveFilters(oldSegment, {
          createdFrom: state.createdFrom,
          createdTo: state.createdTo,
          categories: state.categories ? [...state.categories] : undefined,
          products: state.products ? [...state.products] : undefined,
          contractNumbers: state.contractNumbers
            ? [...state.contractNumbers]
            : undefined,
          sortBy: state.sortBy,
          sortDirection: state.sortDirection,
        });

        state.segment = newSegment;

        // restore filters from the new segment
        const savedFilters = segmentFilterManager.getFilters(newSegment);
        state.createdFrom = savedFilters.createdFrom;
        state.createdTo = savedFilters.createdTo;
        state.categories = savedFilters.categories;
        state.products = savedFilters.products;
        state.contractNumbers = savedFilters.contractNumbers;
        state.sortBy = savedFilters.sortBy as Filter.SortBy | undefined;
        state.sortDirection = savedFilters.sortDirection;
      }
    ),
    changeContractNumbers: baseReducer<
      Filter.ChangeContractNumbersAction['payload']
    >((state, action) => {
      state.contractNumbers = action.payload;
    }),
    deselectContractNumber: baseReducer<
      Filter.DeselectContractNumberAction['payload']
    >((state, action) => {
      state.contractNumbers = state.contractNumbers?.filter(
        (contractNumber) => contractNumber !== action.payload
      );
    }),
    changeProducts: baseReducer<Filter.ChangeProductsAction['payload']>(
      (state, action) => {
        state.products = action.payload;
      }
    ),
    deselectProduct: baseReducer<Filter.DeselectProductAction['payload']>(
      (state, action) => {
        state.products = state.products?.filter(
          (product) => product !== action.payload
        );
      }
    ),
    changeCategories: baseReducer<Filter.ChangeCategoryAction['payload']>(
      (state, action) => {
        state.categories = action.payload;
      }
    ),
    deselectCategory: baseReducer<Filter.DeselectCategoryAction['payload']>(
      (state, action) => {
        state.categories = state.categories?.filter(
          (category) => category !== action.payload
        );
      }
    ),
    changeCreatedFrom: baseReducer<Filter.ChangeCreatedFromAction['payload']>(
      (state, action) => {
        state.createdFrom = action.payload;
      }
    ),
    deselectCreatedFrom: baseReducerNoPayload((state) => {
      state.createdFrom = undefined;
    }),
    changeCreatedTo: baseReducer<Filter.ChangeCreatedToAction['payload']>(
      (state, action) => {
        state.createdTo = action.payload;
      }
    ),
    deselectCreatedTo: baseReducerNoPayload((state) => {
      state.createdTo = undefined;
    }),
    set(state, action: Filter.SetStateAction) {
      return {
        ...state,
        ...action.payload,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      (action: AnyAction) => action.meta?.resetsPage === true,
      (state) => {
        state.page = 1;
      }
    );
  },
});

export const { actions: filterActions } = filterSlice;
export default filterSlice.reducer;
